/**
 * ProviderColumnGrid
 * ──────────────────────────────────────────────────────────────────────
 * Multi-provider time-grid calendar surface used by both the Today view
 * (LegacyEchoScheduler) and the Calendar Day view (SchedulerCalendar).
 *
 * - Time gutter (60px) pinned left.
 * - One sticky column per active provider.
 * - Each appointment block rendered at its correct time slot inside its
 *   provider column. Block style / OB stripe / AI status line / check-in
 *   action are provided by the caller via `renderBlock(apt)`.
 *
 * Pure UI; callers own the data.
 *
 * Props:
 *   providers:      [{ id, name, location, color, todayCount }]
 *   appointments:   [{ id, provider (id), time 'HH:mm', durationMin?, ... }]
 *   hoursStart:     number (default 8)
 *   hoursEnd:       number (default 18)
 *   hourPx:         number (default 56)
 *   gutterWidth:    number (default 60)
 *   renderBlock:    fn(apt) → ReactNode  — the card inside the column cell
 *   renderOpenSlot: fn(apt) → ReactNode  — card for status='open' cells
 *   timeLine?:      ReactNode            — overlay (e.g. CurrentTimeLine)
 *   onAddSlot?:     fn(providerId, 'HH:mm')
 */
import { useMemo } from 'react';
import { cn } from '../../../../lib/utils';
import { Plus } from 'lucide-react';

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const formatHour = (h) => {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
};

// "HH:mm" or "H:mm" → minutes-since-midnight
const parseTimeToMin = (s) => {
  if (!s) return 0;
  const [hStr, mStr] = String(s).trim().split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  return h * 60 + (isNaN(m) ? 0 : m);
};

const ProviderColumnGrid = ({
  providers = [],
  appointments = [],
  hoursStart = 8,
  hoursEnd = 18,
  hourPx = 56,
  gutterWidth = 60,
  minColumnPx = 180,
  renderBlock,
  renderOpenSlot,
  timeLine = null,
  onAddSlot,
  testId = 'provider-grid',
}) => {
  const totalMinutes = (hoursEnd - hoursStart) * 60;
  const pxPerMin = hourPx / 60;
  const bodyHeight = totalMinutes * pxPerMin;

  const hours = useMemo(() => {
    const out = [];
    for (let h = hoursStart; h <= hoursEnd; h += 1) out.push(h);
    return out;
  }, [hoursStart, hoursEnd]);

  // Bucket appointments by provider id
  const byProvider = useMemo(() => {
    const map = {};
    providers.forEach((p) => { map[p.id] = []; });
    appointments.forEach((a) => {
      if (map[a.provider] != null) map[a.provider].push(a);
      else map.__unassigned = (map.__unassigned || []).concat(a);
    });
    return map;
  }, [appointments, providers]);

  const columnCount = Math.max(1, providers.length);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid={testId}
    >
      {/* Sticky header row */}
      <div
        className="grid border-b border-slate-100 bg-white sticky top-0 z-10"
        style={{ gridTemplateColumns: `${gutterWidth}px repeat(${columnCount}, minmax(${minColumnPx}px, 1fr))` }}
      >
        <div className="px-2 py-2 text-[9px] uppercase text-slate-400 font-semibold">Time</div>
        {providers.map((p) => (
          <div
            key={p.id}
            className="px-3 py-2 border-l border-slate-100"
            data-testid={`${testId}-header-${p.id}`}
          >
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: p.color || '#94a3b8' }} />
              <span className="text-[11px] font-semibold text-slate-800 truncate">{p.name}</span>
              {typeof p.todayCount === 'number' && (
                <span className="text-[9px] text-slate-400 ml-auto">({p.todayCount})</span>
              )}
            </div>
            {p.location && (
              <div className="text-[9px] text-slate-400 truncate mt-0.5">{p.location}</div>
            )}
          </div>
        ))}
      </div>

      {/* Scrollable body */}
      <div
        className="relative overflow-y-auto"
        style={{ maxHeight: 560 }}
        data-testid={`${testId}-body`}
      >
        <div
          className="grid relative"
          style={{
            gridTemplateColumns: `${gutterWidth}px repeat(${columnCount}, minmax(${minColumnPx}px, 1fr))`,
            minHeight: bodyHeight,
          }}
        >
          {/* Time gutter */}
          <div className="relative">
            {hours.map((h) => (
              <div
                key={h}
                className="text-[10px] text-slate-400 text-right pr-2"
                style={{
                  position: 'absolute',
                  top: (h - hoursStart) * hourPx - 6,
                  right: 0,
                  width: '100%',
                }}
              >
                {formatHour(h)}
              </div>
            ))}
          </div>

          {/* Provider columns */}
          {providers.map((p) => {
            const apts = (byProvider[p.id] || []);
            return (
              <div
                key={p.id}
                className="relative border-l border-slate-100"
                data-testid={`${testId}-col-${p.id}`}
              >
                {/* Hour grid lines */}
                {hours.map((h) => (
                  <div
                    key={h}
                    className="absolute left-0 right-0 border-t border-slate-50"
                    style={{ top: (h - hoursStart) * hourPx }}
                    aria-hidden
                  />
                ))}
                {/* Empty-slot add affordance per hour */}
                {onAddSlot && hours.slice(0, -1).map((h) => (
                  <button
                    key={`add-${h}`}
                    onClick={() => onAddSlot(p.id, `${pad2(h)}:00`)}
                    className="absolute opacity-0 hover:opacity-100 flex items-center justify-center text-[10px] text-slate-400"
                    style={{
                      top: (h - hoursStart) * hourPx + 4,
                      left: 4,
                      right: 4,
                      height: hourPx - 8,
                      background: 'transparent',
                      border: '1px dashed #e2e8f0',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                    aria-label={`Add appointment at ${formatHour(h)}`}
                    data-testid={`${testId}-add-${p.id}-${pad2(h)}`}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                ))}
                {/* Appointment blocks with horizontal stacking for overlaps */}
                {(() => {
                  // Pre-compute each apt's [start,end] and group overlapping clusters.
                  const items = apts
                    .map((apt) => {
                      const startMin = parseTimeToMin(apt.time) - hoursStart * 60;
                      const dur = apt.durationMin || 30;
                      return { apt, startMin, endMin: startMin + dur, dur };
                    })
                    .filter((it) => it.startMin >= -1 && it.startMin < totalMinutes)
                    .sort((a, b) => a.startMin - b.startMin);

                  // Sweep to assign lane index within each overlap cluster.
                  // Two items overlap if intervals intersect (strict).
                  const clusters = [];
                  let current = [];
                  let currentEnd = -1;
                  items.forEach((it) => {
                    if (it.startMin < currentEnd) {
                      current.push(it);
                      currentEnd = Math.max(currentEnd, it.endMin);
                    } else {
                      if (current.length) clusters.push(current);
                      current = [it];
                      currentEnd = it.endMin;
                    }
                  });
                  if (current.length) clusters.push(current);

                  const rendered = [];
                  clusters.forEach((cluster) => {
                    const laneCount = cluster.length;
                    cluster.forEach((it, laneIdx) => {
                      const { apt, startMin, dur } = it;
                      const top = startMin * pxPerMin + 2;
                      const height = Math.max(36, dur * pxPerMin - 4);
                      const isOpen = apt.status === 'open';
                      // Lane width: split column equally when stacked; 1 lane → full width.
                      const leftPct = laneCount > 1 ? (laneIdx / laneCount) * 100 : 0;
                      const widthPct = laneCount > 1 ? (100 / laneCount) : 100;
                      rendered.push(
                        <div
                          key={apt.id}
                          className={cn('absolute', isOpen && 'opacity-80')}
                          style={{
                            top,
                            height,
                            left: `calc(${leftPct}% + 4px)`,
                            width: `calc(${widthPct}% - 8px)`,
                          }}
                          data-testid={`${testId}-slot-${apt.id}`}
                          data-stack-lane={laneCount > 1 ? `${laneIdx + 1}/${laneCount}` : undefined}
                        >
                          {isOpen && renderOpenSlot ? renderOpenSlot(apt) : (renderBlock ? renderBlock(apt, { laneIdx, laneCount }) : null)}
                        </div>
                      );
                    });
                  });
                  return rendered;
                })()}
              </div>
            );
          })}

          {/* Optional overlay (e.g. CurrentTimeLine) */}
          {timeLine}
        </div>
      </div>
    </div>
  );
};

export default ProviderColumnGrid;
